import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiFilter, FiDownload } from 'react-icons/fi';
import BaselineList from '../../components/Baselines/BaselineList';
import baselineService from '../../services/baselineService';
import './Baselines.css';
import { FaFileExcel } from 'react-icons/fa';

const Baselines = () => {
  const [baselines, setBaselines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    baseline_type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchBaselines();
  }, [filters, pagination.page]);

  const fetchBaselines = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      const data = await baselineService.getAllBaselines(params);
      setBaselines(data.baselines || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      }));
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch baselines');
      console.error('Error fetching baselines:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this baseline?')) {
      try {
        await baselineService.deleteBaseline(id);
        fetchBaselines();
      } catch (err) {
        alert(err.message || 'Failed to delete baseline');
      }
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Are you sure you want to approve this baseline? It will be locked after approval.')) {
      try {
        await baselineService.approveBaseline(id);
        fetchBaselines();
      } catch (err) {
        alert(err.message || 'Failed to approve baseline');
      }
    }
  };

  const handleArchive = async (id) => {
    if (window.confirm('Are you sure you want to archive this baseline?')) {
      try {
        await baselineService.archiveBaseline(id);
        fetchBaselines();
      } catch (err) {
        alert(err.message || 'Failed to archive baseline');
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDownloadBaselineList = () => {
    if (!baselines || baselines.length === 0) {
      alert('No baselines to download');
      return;
    }

    const headers = [
      'Name', 'Version', 'Type', 'Status', 'Created At'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = baselines.map((baseline) => [
      baseline.name,
      baseline.version,
      baseline.baseline_type,
      baseline.status,
      baseline.createdAt ? new Date(baseline.createdAt).toLocaleDateString() : ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `baselines-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="baselines-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Baselines</h1>
          <p>Manage project baselines for scope, schedule, and cost tracking</p>
        </div>
        <div className="header-actions">
          <Link to="/app/baselines/create" className="btn btn-primary">
            <FiPlus /> Create Baseline
          </Link>
          <button
            onClick={handleDownloadBaselineList}
            className="px-4 py-4 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download baseline list"
          >
            <FaFileExcel />
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filters-section">
          <div className="filters-header">
            {/* <FiFilter /> */}
            <span>Filters</span>
          </div>
          <div className="filters-content">
            <div className="filter-group">
              <input
                type="text"
                id="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by name or version..."
              />
            </div>
            <div className="filter-group">
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
                <option value="Superseded">Superseded</option>
              </select>
            </div>
            <div className="filter-group">
              <select
                id="baseline_type"
                name="baseline_type"
                value={filters.baseline_type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="Initial">Initial</option>
                <option value="Revised">Revised</option>
                <option value="Current">Current</option>
                <option value="Final">Final</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading baselines...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={fetchBaselines} className="btn btn-primary">
              Retry
            </button>
          </div>
        ) : (
          <>
            <BaselineList
              baselines={baselines}
              onDelete={handleDelete}
              onApprove={handleApprove}
              onArchive={handleArchive}
            />

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Baselines;


